import {
  Form,
  Gap,
  Grid,
  Heading,
  Input,
  Alert,
  List,
  P,
  SingleColumnLayout,
  Span,
  Textarea,
  ListItem,
  ListItemMeta,
} from '@salecycle/ui-component-library';
import { useUserContext } from 'core/users/UserContext';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEditRole } from '../context/EditRoleProvider';
import { EditRoleSidebar } from './EditRoleSidebar';

export const EditRoleForm: React.FC = () => {
  const {
    originalRole,
    roleName,
    roleDescription,
    roleUsers,
    roleNameValid,
    roleDescriptionValid,
    setRoleName,
    setRoleDescription,
    rolePermissions,
  } = useEditRole();
  const { getUser } = useUserContext();
  const { t } = useTranslation();
  const setName = (name: string) => setRoleName(name);
  const setDescription = (description: string) => setRoleDescription(description);

  return (
    <SingleColumnLayout
      paddingLeft={20}
      heading={t('userManagement.roles.edit.form.heading', {
        roleName: originalRole?.name ?? '',
      })}
      showSidebar={true}
      sidebar={<EditRoleSidebar />}
      sidebarHeading={t('userManagement.roles.edit.sidebar.heading')}
    >
      <Grid columnNumber={1}>
        <P>
          {t('userManagement.roles.edit.description', {
            roleName: originalRole?.name ?? '',
          })}
        </P>
        <Form>
          <Input
            ariaLabel={t('userManagement.roles.edit.form.roleName.ariaLabel')}
            value={roleName}
            onChange={setName}
            label={t('userManagement.roles.edit.form.roleName.label')}
            validationState={roleNameValid ? 'complete' : 'warning'}
            validationMessage={roleNameValid ? null : t('userManagement.roles.edit.form.roleName.validationMessage')}
          />
          <Textarea
            ariaLabel={t('userManagement.roles.edit.form.roleDescription.ariaLabel')}
            value={roleDescription}
            onChange={setDescription}
            label={t('userManagement.roles.edit.form.roleDescription.label')}
            validationState={roleDescriptionValid ? 'complete' : 'warning'}
            validationMessage={
              roleDescriptionValid ? null : t('userManagement.roles.edit.form.roleDescription.validationMessage')
            }
          />
          {rolePermissions && rolePermissions.length === 0 && (
            <>
              <Gap top={20} />
              <Alert variation="warning" heading={t('userManagement.roles.edit.form.permissions.validationHeading')}>
                {t('userManagement.roles.edit.form.permissions.validationMessage')}
              </Alert>
              <Gap top={20} />
            </>
          )}
          {rolePermissions?.length > 0 && <Gap top={40} />}

          <Heading variation="h2">{t('userManagement.roles.edit.form.usersSection.heading')}</Heading>
          <Span>{t('userManagement.roles.edit.form.usersSection.description')}</Span>
          <Gap top={30} />

          <List>
            {roleUsers.map(userId => (
              <ListItem key={`user-${userId}`} variation="full-border">
                <ListItemMeta avatarIcon="users" heading={getUser(userId)?.name ?? ''} />
              </ListItem>
            ))}
          </List>
        </Form>
      </Grid>
    </SingleColumnLayout>
  );
};
